import React from 'react';
import { DetailSectionModularProps } from 'component/page/detail/DetailCard/DetailsCard.selectors';
import Feature from 'component/common/Experiment/Feature';
import { FeatureName } from 'core/constant/FeatureName';
import { Box, Rating, TextLink, Typography } from '@drone-js/core';
import ActivityCardBadge from 'component/common/Badge/ActivityCardBadge';
import { ActivityBadgeCategory, ActivityBadgeCategoryProps } from 'component/common/Badge/BadgeService.types';
import BookingCounter from 'component/common/BookingCounter/BookingCounter';
import { StringFormatter } from '@agoda/react-handyman';
import { TitleSectionVariant } from 'component/page/detail/DetailPageModularize/detailPageModularize.types';
import { useSelector } from 'react-redux';
import { selectCmsFromBootstrap } from 'store/slice/bootstrap';
import { REVIEW_SECTION_HREF } from 'component/page/detail/DetailPageModularize/contentRegistry.type';
import TypographyWithWordBreak from 'component/common/TypographyWithWordBreak/TypographyWithWordBreak';

const BADGE_CATEGORY_PROPS: ActivityBadgeCategoryProps = {
    maxBadges: { s: 5, m: 5, l: 5 },
    showSummary: { s: false, m: false, l: false },
    boxMargin: {
        s: { mt: '4', me: '4' },
        m: { mt: '4', me: '4' },
        l: { mt: '4', me: '4' },
    },
    badgeMargin: {
        s: { mb: '4', me: '4' },
        m: { mb: '4', me: '4' },
        l: { mb: '4', me: '4' },
    },
};

export const HighlightBadgesNodeComponent: React.FC<{ isPageContainerSpacing: boolean } & Pick<DetailSectionModularProps, 'activityId' | 'badges'>> = (props) => {
    const highlightBadges = props.badges?.activityHighlights || [];
    return (
        <Feature.On name={FeatureName.BadgesV2DetailsPage}>
            <Box display="flex" mt={props.isPageContainerSpacing ? undefined : '8'}>
                <ActivityCardBadge
                    badges={highlightBadges}
                    activityId={props.activityId}
                    badgeCategory={ActivityBadgeCategory.HIGHLIGHT}
                    isBVariant={true}
                    overrideCategoryProps={BADGE_CATEGORY_PROPS}
                />
            </Box>
        </Feature.On>
    );
};
HighlightBadgesNodeComponent.displayName = 'HighlightBadgesNodeComponent';

export const KeyAttributeBadgesNodeComponent: React.FC<Pick<DetailSectionModularProps, 'activityId' | 'badges'>> = (props) => {
    return (
        <ActivityCardBadge
            badges={(props.badges && props.badges.keyAttributes) || []}
            badgeCategory={ActivityBadgeCategory.KEY_ATTRIBUTES}
            activityId={props.activityId}
            isBVariant={true}
        />
    );
};
KeyAttributeBadgesNodeComponent.displayName = 'KeyAttributeBadgesNodeComponent';

export const BookingCounterNodeComponent: React.FC<Pick<DetailSectionModularProps, 'badges' | 'reviewAverageScore' | 'reviewTotalReview'>> = (props) => {
    const bookInfoBadges = props.badges ? props.badges.bookingInfo || [] : [];
    return (
        <Feature.On name={FeatureName.BadgesV2DetailsPage}>
            <Box display="inline-flex" flexDirection="row">
                <BookingCounter showSeparator={!!props.reviewAverageScore && !!props.reviewTotalReview} badges={bookInfoBadges} />
            </Box>
        </Feature.On>
    );
};
BookingCounterNodeComponent.displayName = 'BookingCounterNodeComponent';

export const SectionTitleComponent: React.FC<{ isPageContainerSpacing: boolean } & Pick<DetailSectionModularProps, 'contentLocale' | 'title'>> = (props) => {
    return (
        <>
            <Feature.On name={FeatureName.WordBreakOnDetailPage}>
                <TypographyWithWordBreak
                    data-testid="details-title"
                    variant="heading-default"
                    tag="h1"
                    textAlign="start"
                    mt={props.isPageContainerSpacing ? undefined : '12'}
                    mb="0"
                    lang={props.contentLocale}
                >
                    {props.title}
                </TypographyWithWordBreak>
            </Feature.On>
            <Feature.Off name={FeatureName.WordBreakOnDetailPage}>
                <Typography
                    data-testid="details-title"
                    variant="heading-default"
                    tag="h1"
                    textAlign="start"
                    mt={props.isPageContainerSpacing ? undefined : '12'}
                    mb="0"
                    lang={props.contentLocale}
                >
                    {props.title}
                </Typography>
            </Feature.Off>
        </>
    );
};
SectionTitleComponent.displayName = 'SectionTitleComponent';

type ReviewSummaryNodeComponentType = Pick<DetailSectionModularProps, 'cms' | 'contentLocale' | 'onSeeReviewClick' | 'reviewAverageScore' | 'reviewTotalReview' | 'variant'>;
export const ReviewSummaryNodeComponent: React.FC<ReviewSummaryNodeComponentType> = (props) => {
    const cms = useSelector(selectCmsFromBootstrap);
    const reviewsAvailable = !!props.reviewAverageScore && !!props.reviewTotalReview;
    const reviewScore = Number(props.reviewAverageScore);
    const REVIEW_RATING = StringFormatter.format(cms[224212], reviewScore) || 'Review star rating';
    if (!reviewsAvailable) {
        return null;
    }

    const seeReviewText = props.reviewTotalReview ? StringFormatter.format(props.cms[168191], props.reviewTotalReview.toLocaleString(props.contentLocale)) : '';

    return (
        <Box data-testid="activities-details-review-summary" display="inline-flex" flexDirection="row" alignItems="center">
            <Box display="inline-flex">
                <Rating data-testid="review-star-rating" label={REVIEW_RATING} score={reviewScore} size="m" variant="star" color="hotel" me="4" />
            </Box>
            <Typography data-testid="review-score" variant="heading-default" textColor="product.rating-hotels" pe="4">
                {props.reviewAverageScore}
            </Typography>
            {props.variant === TitleSectionVariant.NoThumbnail ? (
                <TextLink
                    data-element-name="activities-see-review-textlink"
                    data-testid="activities-see-review-textlink"
                    label={seeReviewText}
                    onClick={() => props.onSeeReviewClick(REVIEW_SECTION_HREF)}
                    textVariant="body-s"
                />
            ) : (
                <Typography
                    data-element-name="activities-see-review-textlink"
                    data-testid="activities-see-review-textlink"
                    display="inline-flex"
                    key="totalCount"
                    variant="caption-default"
                    textColor="base.subtle"
                >
                    {seeReviewText}
                </Typography>
            )}
        </Box>
    );
};
ReviewSummaryNodeComponent.displayName = 'ReviewSummaryNodeComponent';
